import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BehaviorSubject, debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { IGenericLabelValue } from '@shared/interface/generic';
import { InputSelectMaterialInterface } from '@shared/components/form/input-select-material/input-select-material.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '@services/base-service.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { IListUserAssignment } from '../../../pages/adminsettings/users/users.interface';
import { ISelectedOption } from '@shared/components/input-select-scope-role/input-select-scope-role.interface';
import { EnumRole, EnumRoleLabel } from '@models/role.model';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-input-select-scope-role',
  templateUrl: './input-select-scope-role.component.html',
  styleUrls: ['./input-select-scope-role.component.scss'],
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: 'modal-autocomplete-panel',
      },
    },
  ],
})
export class InputSelectScopeRoleComponent implements OnInit, OnDestroy {
  @Input() label = '';
  @Input() icon = '';

  @Input() placeholder = '';
  @Input() endpointUrl = '';
  @Input() selectedValue: string[] = [];

  @Input() usePagination = true;
  @Input() disabled = false;
  @Input() isOptional = true;
  @Input() enumRole: EnumRole;
  @Input() roleValue: string = 'Super Admin';

  @Input() areaValue: BehaviorSubject<IGenericLabelValue[]> = new BehaviorSubject([] as IGenericLabelValue[]);
  @Input() warehouseValue: BehaviorSubject<IGenericLabelValue[]> = new BehaviorSubject([] as IGenericLabelValue[]);
  @Output() selectedValues = new EventEmitter<ISelectedOption>();

  id: string = '';
  initOptionsSubject: BehaviorSubject<InputSelectMaterialInterface[]> = new BehaviorSubject([] as InputSelectMaterialInterface[]);
  optionsSubject: BehaviorSubject<InputSelectMaterialInterface[]> = new BehaviorSubject([] as InputSelectMaterialInterface[]);
  selectedOptions: BehaviorSubject<ISelectedOption[]> = new BehaviorSubject([] as ISelectedOption[]);

  form = this.fb.group({
    areas: this.fb.array([]),
    employee: new FormControl('', Validators.required),
  });
  searchControl = new FormControl();
  loadingSubject = new BehaviorSubject(false);
  checkedRole = new BehaviorSubject(false);

  currentPage = 0;
  pageSize = 20;
  hasMoreData = true;

  get SearchControl() {
    return this.searchControl;
  }

  get DataForm() {
    return this.form as FormGroup;
  }

  get AreaFormArray() {
    return this.form.controls.areas as FormArray;
  }

  AreaFromArray(index: number) {
    return this.AreaFormArray.at(index).get('area') as FormControl;
  }

  EmployeeFromArray(index: number) {
    return this.AreaFormArray.at(index).get('employee') as FormControl;
  }

  get EmployeeForm() {
    return this.form.controls.employee as FormControl;
  }

  private unsubscribe: Subscription[] = [];

  constructor(
    private baseService: BaseService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute
  ) {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.id = params.get('id') ?? '';
    });
  }

  ngOnInit() {
    this.fetchInitialData();
    // this.handleAreaValue();
    this.handleCheckRole();
    this.initSearchControl();
  }

  fetchInitialData() {
    this.loadingSubject.next(true);
    const fetchData = this.fetchSearchOptions().subscribe((data: InputSelectMaterialInterface[]) => {
      if (!this.initOptionsSubject.value.length) {
        this.initOptionsSubject.next(data);
      }
      this.optionsSubject.next(data);
      this.loadingSubject.next(false);
      this.cdr.detectChanges();
    });
    this.unsubscribe.push(fetchData);
  }

  setAreaValue(value: IGenericLabelValue[]) {
    value.flatMap((item) => {
      this.AreaFormArray.push(
        this.fb.group({
          area: this.fb.control(item, [Validators.required]),
          employee: this.fb.control('', [Validators.required]),
        })
      );
    });
  }

  // handleAreaValue() {
  //   this.areaValue.subscribe(value => {
  //     if (!value) return;
  //     this.AreaFormArray.clear();
  //     this.setAreaValue(value);
  //   })
  // }

  handleCheckRole() {
    this.checkedRole.subscribe((value) => {
      this.AreaFormArray.clear();
      this.EmployeeForm.reset();
      if (value) {
        this.setAreaValue(this.areaValue.value);
        this.EmployeeForm.clearValidators();
      } else {
        this.EmployeeForm.setValidators(Validators.required);
      }
      this.selectedValues.emit(this.form.value as unknown as ISelectedOption);
    });
  }

  initSearchControl() {
    if (this.disabled) return this.searchControl.disable();
    this.EmployeeForm.valueChanges
      .pipe(
        debounceTime(250),
        tap(() => this.loadingSubject.next(true)),
        switchMap((query: string) => {
          return this.handleQueryInput(query);
        })
      )
      .subscribe((data) => {
        this.optionsSubject.next(data);
        this.loadingSubject.next(false);
      });
  }

  handleQueryInput(query: string) {
    // console.log('query', query)
    if (query && query.length >= 3) {
      // Fetch filtered data based on query
      return this.fetchSearchOptions(query, this.currentPage);
    } else if (!query) {
      // Reset to default data if query is empty
      this.resetState();
      // console.log('initOptionsSubject', this.initOptionsSubject.value)
      return of(this.initOptionsSubject.value);
    } else {
      // console.log('else')
      return of(this.initOptionsSubject.value);
    }
  }

  fetchSearchOptions = (query = '', page = this.currentPage) => this.fetchData(query, page);

  fetchData(query: string, page: number) {
    let url = this.endpointUrl + this.id;

    if (this.hasSearchKeyword(query)) url += `?string_filter=${query.trim()}`;
    if (this.usePagination && this.currentPage > 0) url += this.hasSearchKeyword(query) ? `&page=${page}` : `?page=${page}`;

    return this.baseService.getData<IListUserAssignment[]>(url).pipe(
      switchMap((resp) => {
        if (resp && resp.data.length) {
          this.hasMoreData = this.usePagination && !!(resp && resp.data.length);
          const data = resp && resp.data;
          this.optionsSubject.next(data as InputSelectMaterialInterface[]);

          return of(this.optionsSubject.value);
        } else {
          return of([]);
        }
      })
    );
  }

  onOptionSelected(e: MatAutocompleteSelectedEvent) {
    if (this.disabled) return;

    const selectedOption = e.option.value as IGenericLabelValue;

    // add selected option
    const _check = this.selectedOptions.value.some((option) => {
      if (option.employee) {
        return option.employee.value === selectedOption.value;
      } else {
        return option.areas?.flatMap((val) => val.area.value).includes(selectedOption.value);
      }
    });
    if (!_check) {
      const _selected = {} as ISelectedOption;
      this.selectedOptions.next([...this.selectedOptions.value, _selected]);
    }

    // emit selected values
    this.selectedValues.emit(this.form.value as unknown as ISelectedOption);
  }

  resetState() {
    this.currentPage = 0;
    this.hasMoreData = true;
  }

  displayFn(option: InputSelectMaterialInterface) {
    // console.log('displayFn ', option);
    // return option?.label;
    return option?.label ?? '';
  }

  searchNotFound = () => {
    const searchControl = this.searchControl.value?.value;
    // && this.searchControl.value?.value.length >= 3
    return !this.optionsSubject.value.length && this.hasSearchKeyword(searchControl);
  };

  hasSearchKeyword = (q: string) => {
    return !!(q && q.trim());
  };

  handleChecked(e: boolean) {
    this.checkedRole.next(e);
  }

  // Subscribe to value changes for the newly added control
  subscribeEmployeeValueChanges(index: number): void {
    const control = this.AreaFormArray.at(index).get('employee') as FormControl;
    control.valueChanges
      .pipe(
        debounceTime(1000),
        tap(() => this.loadingSubject.next(true)),
        switchMap((query: string) => {
          return this.handleQueryInput(query);
        })
      )
      .subscribe((data) => {
        this.optionsSubject.next(data);
        this.loadingSubject.next(false);
      });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
  protected readonly FormControl = FormControl;
  protected readonly EnumRole = EnumRole;
  protected readonly EnumRoleLabel = EnumRoleLabel;
}
